import { Box, Button, MenuItem, TextField } from "@mui/material"
import {
  get_number_geojson_properties,
  spatial_merge,
} from "../../utils/layer_actions"
import { useEffect, useState } from "react"
import axios from "axios"
import * as turf from "@turf/turf"
import { toast } from "react-toastify"
import HandleFail from "../../utils/error_handler"
import feature_schema from "../../configs/feature_schema.json"

const get_ward_counts = (analysis_units) => {
  const ward_counts = {}
  analysis_units.features.forEach(
    ({ properties: { ward_id, cup_id, population } }) => {
      ward_counts[ward_id] ??= {}
      ward_counts[ward_id][cup_id] ??= 0
      ward_counts[ward_id][cup_id] += population
    },
  )
  return ward_counts
}

const max_key = (obj) => {
  let maxK = null
  let maxV = -Infinity
  for (const [k, v] of Object.entries(obj)) {
    if (v > maxV) {
      maxV = v
      maxK = k
    }
  }
  return maxK
}

const update_au_to_majority = (analysis_units, ward_to_cup) => {
  analysis_units.features.forEach((feature) => {
    const ward_id = feature.properties.ward_id
    const cup_id = ward_to_cup[ward_id]
    if (cup_id != null) {
      feature.properties.cup_id = cup_id
    }
  })
}

const majority_cup_per_ward = (ward_counts) =>
  Object.fromEntries(
    Object.entries(ward_counts).map(([ward_id, cup_counts]) => [
      ward_id,
      Number(max_key(cup_counts)),
    ]),
  )

const majority_ward_assign = (analysis_units) => {
  const ward_counts = get_ward_counts(analysis_units)
  const ward_to_cup = majority_cup_per_ward(ward_counts)
  update_au_to_majority(analysis_units, ward_to_cup)
}

const determine_cup_boundary = async ({
  map_data,
  max_core_distance,
  max_remote_distance,
  au_pop_property,
  allow_partial_allocations,
  run_second_pass,
  dispatch_map_data,
  cup_capacity,
  dispatch_ui,
}) => {
  dispatch_ui({ type: "START_LOADING" })
  await HandleFail(async () => {
    let { cup_geo_json, au_geo_json } = map_data

    cup_geo_json = spatial_merge(cup_geo_json, au_geo_json)

    const fixed_cups = new Set(
      cup_geo_json.features
        .filter((feature) => feature.properties.fixed)
        .map((feature) => feature.id),
    )

    const cups = Object.fromEntries(
      cup_geo_json.features
        .filter((feature) => !feature.properties.fixed)
        .filter(
          (feature) =>
            feature.properties[feature_schema.facility.au_id_property] !==
              null &&
            feature.properties[feature_schema.facility.au_id_property] !==
              undefined,
        )
        .map((feature) => [
          feature.id,
          {
            au_id: feature.properties[feature_schema.facility.au_id_property],
            capacity: parseInt(cup_capacity),
          },
        ]),
    )

    const analysis_units = Object.fromEntries(
      au_geo_json.features
        .filter((feature) => !fixed_cups.has(feature.properties.cup_id))
        .map((feature) => [
          feature.properties[feature_schema.au.id_property],
          {
            demand: feature.properties[au_pop_property],
            urban_core: feature.properties[feature_schema.au.core_property],
          },
        ]),
    )

    const updated_au_geo_json = {
      ...au_geo_json,
      features: au_geo_json.features.map((f) => ({ ...f })),
    }

    if (Object.values(cups).length === 0) {
      throw Error("No unfixed cups")
    }

    const { data } = await axios.post("/api/allocation", {
      max_core_distance,
      max_remote_distance,
      facilities: cups,
      analysis_units,
      allow_partial_allocations,
      run_second_pass,
    })
    const analysis_units_results = data.analysis_units
    updated_au_geo_json.features = [...au_geo_json.features]
    updated_au_geo_json.features.forEach((feature) => {
      const au_id = feature.properties[feature_schema.au.id_property]
      if (!analysis_units_results[au_id]) {
        return
      }
      let { catchment } = analysis_units_results[au_id]
      feature.properties.cup_id =
        catchment === null ? -1 : parseInt(catchment[0])
    })

    majority_ward_assign(updated_au_geo_json)

    const dissolved = turf.dissolve(
      turf.featureCollection(updated_au_geo_json.features),
      {
        propertyName: "cup_id",
      },
    )
    dissolved.features.forEach((feature) => {
      feature.id = parseInt(feature.properties.cup_id)
      feature.properties.cup_id = parseInt(feature.properties.cup_id)
    })

    dispatch_map_data({
      type: "SET_DATA",
      payload: {
        cup_geo_json,
        au_geo_json: updated_au_geo_json,
        cup_boundary_geo_json: dissolved,
      },
    })

    toast.success("CUP boundaries updated")
  })
  dispatch_ui({ type: "STOP_LOADING" })
}

const run_optimisation = async ({
  map_data,
  dispatch_map_data,
  number_of_recommendations,
  capacity_of_recommendations,
  core_distance,
  au_pop_property,
  num_iterations,
}) => {
  if (!au_pop_property) {
    throw Error("Demand property is required.")
  }

  let { cup_geo_json, au_geo_json } = map_data

  const fixed_cups = cup_geo_json
    ? cup_geo_json.features.filter((feature) => feature.properties.fixed)
    : []

  const fixed_cup_ids = fixed_cups.map((feature) => parseInt(feature.id))

  const demand_array = au_geo_json.features
    .filter(
      (feature) => !fixed_cup_ids.includes(parseInt(feature.properties.cup_id)),
    )
    .map((feature) => {
      return {
        demand: Math.trunc(feature.properties[au_pop_property]),
        id: feature.properties[feature_schema.au.id_property],
        valid_location: feature.properties[feature_schema.au.core_property],
        urban_core: feature.properties[feature_schema.au.core_property],
      }
    })

  const optimisation_parameters = {
    number_of_recommendations,
    capacity_of_recommendations,
    core_distance,
    non_core_distance: core_distance,
    demand_array,
    num_iterations,
  }

  const response = await axios.post(
    "/api/optimisation/",
    optimisation_parameters,
  )
  let { data } = response

  const au_ids = data.recommendations.map((recommendation) => {
    const { au_id } = recommendation
    return au_id
  })

  dispatch_map_data({
    type: "CREATE_CUPS_BATCH",
    payload: {
      au_ids,
    },
  })

  toast.success("Optimisation ran successfully")
}

const CUPS = ({ map_data, dispatch_map_data, dispatch_ui }) => {
  const [core_distance, set_core_distance] = useState(5000)
  const [au_pop_property, set_au_pop_property] = useState("")
  const [number_of_recommendations, set_number_of_recommendations] =
    useState(10)
  const [capacity_of_recommendations, set_capacity_of_recommendations] =
    useState(200000)
  const [num_iterations, set_num_iterations] = useState(100)
  const { au_geo_json } = map_data

  useEffect(() => {
    if (!au_pop_property && au_geo_json) {
      const options = get_number_geojson_properties(au_geo_json)
      if (options.includes("population")) {
        set_au_pop_property("population")
      } else {
        set_au_pop_property(options[0])
      }
    }
  }, [au_pop_property, au_geo_json])

  return (
    <Box sx={{ width: "100%", marginTop: 2 }}>
      <TextField
        label="Distance Constraint (meters)"
        fullWidth
        type="number"
        variant="standard"
        value={core_distance}
        onChange={(event) => {
          const input_value = event.target.value
          if (input_value >= 0) {
            set_core_distance(input_value)
          }
        }}
      />

      <TextField
        label="Number of CUPS"
        fullWidth
        type="number"
        variant="standard"
        value={number_of_recommendations}
        onChange={(event) => {
          const input_value = event.target.value
          if (input_value >= 0) {
            set_number_of_recommendations(input_value)
          }
        }}
      />

      <TextField
        label="CUP Size"
        fullWidth
        type="number"
        variant="standard"
        value={capacity_of_recommendations}
        onChange={(event) => {
          const input_value = event.target.value
          if (input_value >= 0) {
            set_capacity_of_recommendations(input_value)
          }
        }}
      />

      <TextField
        fullWidth
        select
        label="Population Type"
        type="text"
        variant="standard"
        value={au_pop_property}
        onChange={(event) => {
          const input_value = event.target.value
          set_au_pop_property(input_value)
        }}
      >
        {get_number_geojson_properties(au_geo_json)
          .sort()
          .map((type) => {
            return (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            )
          })}
      </TextField>

      <TextField
        label="Number of Iterations"
        fullWidth
        type="number"
        variant="standard"
        value={num_iterations}
        onChange={(event) => {
          const input_value = event.target.value
          if (input_value >= 0) {
            set_num_iterations(input_value)
          }
        }}
      />

      <Button
        onClick={async () => {
          dispatch_ui({ type: "START_LOADING" })
          await HandleFail(async () => {
            if (core_distance > 650000) {
              throw new Error(
                "Distance of more than 65km is currently not supported.",
              )
            }
            await run_optimisation({
              map_data,
              dispatch_map_data,
              number_of_recommendations,
              capacity_of_recommendations,
              core_distance,
              au_pop_property,
              num_iterations,
            })
          })
          dispatch_ui({ type: "STOP_LOADING" })
        }}
        sx={{ marginTop: 1 }}
        variant="outlined"
        fullWidth
      >
        Calculate CUPS
      </Button>

      <Button
        onClick={() => {
          determine_cup_boundary({
            map_data,
            dispatch_ui,
            max_core_distance: 1000000,
            max_remote_distance: 1000000,
            au_pop_property,
            allow_partial_allocations: true,
            run_second_pass: false,
            cup_capacity: capacity_of_recommendations,
            dispatch_map_data,
          })
        }}
        sx={{ marginTop: 1 }}
        variant="outlined"
        fullWidth
      >
        CUP Boundary
      </Button>
    </Box>
  )
}

export default CUPS
